// ---------------------------------------------------------------------------
// supportService.js — client for the Support (Freshdesk) backend endpoints.
//
// Routes through the shared axios instance (src/api.js), so the JWT is
// attached automatically on every call. No Freshdesk domain, key, or
// account secret is ever referenced here — the frontend only ever talks to
// our own /api/freshdesk/* controller, which holds the credentials.
//
// All functions return response.data; callers handle errors. The structured
// backend error shape is { error: "<code>", message: "<safe text>" } and is
// surfaced via the helper below.
// ---------------------------------------------------------------------------
import api from "../../api";

const BASE = "/freshdesk";

/** Pull the structured { error, message } out of an axios error. */
export const parseSupportError = (err) => {
    const data = err?.response?.data;
    const code = data?.error || "upstream";
    const message = data?.message || "Something went wrong. Please try again.";
    const status = err?.response?.status;
    const retryAfter = data?.retryAfter ?? null;
    return { code, message, status, retryAfter };
};

// ── Accounts + meta ────────────────────────────────────────────────────────
export const getAccounts = async () => {
    const res = await api.get(`${BASE}/accounts`);
    return Array.isArray(res.data) ? res.data : [];
};

export const getMeta = async () => {
    const res = await api.get(`${BASE}/meta`);
    return res.data;
};

// Account-scoped meta: REAL status/priority choices (incl. custom statuses)
// for the given account, plus the caller's access flag.
export const getAccountMeta = async (accountKey) => {
    const res = await api.get(`${BASE}/${accountKey}/meta`);
    return res.data;
};

// Account-wide ticket counts per status label (for the summary tiles).
export const getCounts = async (accountKey) => {
    const res = await api.get(`${BASE}/${accountKey}/counts`);
    return res.data || {};
};

// ── Tickets ──────────────────────────────────────────────────────────────
export const listTickets = async (accountKey, params = {}) => {
    const res = await api.get(`${BASE}/${accountKey}/tickets`, { params });
    return res.data; // FreshdeskPagedResult
};

export const searchTickets = async (accountKey, q, page = 1) => {
    const res = await api.get(`${BASE}/${accountKey}/tickets/search`, {
        params: { q, page },
    });
    return res.data;
};

export const getTicket = async (accountKey, ticketId) => {
    const res = await api.get(`${BASE}/${accountKey}/tickets/${ticketId}`);
    return res.data; // FreshdeskTicketDetailDto (includes conversations)
};

export const getConversations = async (accountKey, ticketId) => {
    const res = await api.get(
        `${BASE}/${accountKey}/tickets/${ticketId}/conversations`
    );
    return Array.isArray(res.data) ? res.data : [];
};

export const createTicket = async (accountKey, payload) => {
    const res = await api.post(`${BASE}/${accountKey}/tickets`, payload);
    return res.data;
};

export const replyToTicket = async (accountKey, ticketId, body) => {
    const res = await api.post(
        `${BASE}/${accountKey}/tickets/${ticketId}/reply`,
        { body }
    );
    return res.data;
};

export const updateTicket = async (accountKey, ticketId, payload) => {
    const res = await api.put(
        `${BASE}/${accountKey}/tickets/${ticketId}`,
        payload
    );
    return res.data;
};

// ── Lookups ────────────────────────────────────────────────────────────────
export const getGroups = async (accountKey) => {
    const res = await api.get(`${BASE}/${accountKey}/groups`);
    return Array.isArray(res.data) ? res.data : [];
};

export const getAgents = async (accountKey) => {
    const res = await api.get(`${BASE}/${accountKey}/agents`);
    return Array.isArray(res.data) ? res.data : [];
};
